/**
 * Central registry of all guides/instructions.
 * To add a new guide:
 * 1. Create a TSX component in src/pages/guides/
 * 2. Add an entry here with route, tags, and i18n titles
 * 3. Import and register the route in App.tsx
 *
 * The Help Center page will automatically list all guides from this registry.
 * The FAQ search will also search through guide titles and tags.
 */
export const guideCategories = [
    { id: 'weather', label: { en: 'Weather', de: 'Wetter', fr: 'Météo', es: 'Clima', it: 'Meteo', pt: 'Clima', nl: 'Weer', pl: 'Pogoda', cs: 'Počasí', ja: '天気', ko: '날씨', zh: '天气', ru: 'Погода', uk: 'Погода' }, icon: '🌤️' },
    { id: 'tides', label: { en: 'Tides', de: 'Gezeiten', fr: 'Marées', es: 'Mareas', it: 'Maree', pt: 'Marés', nl: 'Getijden', pl: 'Pływy', cs: 'Přílivy', ja: '潮汐', ko: '조수', zh: '潮汐', ru: 'Приливы', uk: 'Припливи' }, icon: '🌊' },
    { id: 'gps', label: { en: 'GPS & Location', de: 'GPS & Standort', fr: 'GPS et position', es: 'GPS y ubicación', it: 'GPS e posizione', pt: 'GPS e localização', nl: 'GPS en locatie', pl: 'GPS i lokalizacja', cs: 'GPS a poloha', ja: 'GPSと位置', ko: 'GPS 및 위치', zh: 'GPS与位置', ru: 'GPS и местоположение', uk: 'GPS та місце розташування' }, icon: '📍' },
    { id: 'setup', label: { en: 'Setup', de: 'Einrichtung', fr: 'Configuration', es: 'Configuración', it: 'Configurazione', pt: 'Configuração', nl: 'Instellingen', pl: 'Ustawienia', cs: 'Nastavení', ja: '設定', ko: '설정', zh: '设置', ru: 'Настройка', uk: 'Налаштування' }, icon: '⚙️' },
    { id: 'purchase', label: { en: 'Purchase', de: 'Kauf', fr: 'Achat', es: 'Compra', it: 'Acquisto', pt: 'Compra', nl: 'Aankoop', pl: 'Zakup', cs: 'Nákup', ja: '購入', ko: '구매', zh: '购买', ru: 'Покупка', uk: 'Придбання' }, icon: '💳' },
    { id: 'fonts', label: { en: 'Fonts', de: 'Schriften', fr: 'Polices', es: 'Fuentes', it: 'Caratteri', pt: 'Fontes', nl: 'Lettertypen', pl: 'Czcionki', cs: 'Písma', ja: 'フォント', ko: '글꼴', zh: '字体', ru: 'Шрифты', uk: 'Шрифти' }, icon: '🔤' },
    { id: 'general', label: { en: 'General', de: 'Allgemein', fr: 'Général', es: 'General', it: 'Generale', pt: 'Geral', nl: 'Algemeen', pl: 'Ogólne', cs: 'Obecné', ja: '一般', ko: '일반', zh: '通用', ru: 'Общее', uk: 'Загальне' }, icon: '📖' },
];
export const guidesRegistry = [
    {
        id: 'purchase',
        route: '/guides/purchase',
        icon: '💳',
        category: 'purchase',
        tags: ['purchase', 'buy', 'vx', 'elite', 'garmin pay', 'installation', 'install', 'unlock code', 'key', 'zahlung', 'kauf', 'freischaltcode'],
        title: {
            en: 'Purchase & Installation (VX/Elite explanation)',
            de: 'Kauf & Installation (VX/Elite Erklärung)',
            fr: 'Achat et installation (explication VX/Elite)',
            es: 'Compra e instalación (explicación VX/Elite)',
            it: 'Acquisto e installazione (spiegazione VX/Elite)',
            pt: 'Compra e instalação (explicação VX/Elite)',
            nl: 'Koop en installatie (VX/Elite uitleg)',
            pl: 'Zakup i instalacja (wyjaśnienie VX/Elite)',
            cs: 'Nákup a instalace (vysvětlení VX/Elite)',
            ja: '購入とインストール（VX/Elite説明）',
            ko: '구매 및 설치 (VX/Elite 설명)',
            zh: '购买与安装（VX/Elite 说明）',
            ru: 'Покупка и установка (объяснение VX/Elite)',
            uk: 'Придбання та інсталяція (пояснення VX/Elite)'
        },
        description: {
            en: 'How to install watchfaces, difference between VX and Elite versions, and how purchasing works',
            de: 'Installation, Unterschied zwischen VX- und Elite-Versionen und Details zum Kauf',
            fr: 'Installer les watchfaces, différence VX/Elite et modalités d\'achat',
            es: 'Instalar watchfaces, diferencia entre VX y Elite, y proceso de compra',
            it: 'Installare i watchface, differenza VX/Elite e come funziona l\'acquisto',
            pt: 'Instalar watchfaces, diferença entre VX e Elite e como comprar',
            nl: 'Watchfaces installeren, verschil VX en Elite, en aankoop',
            pl: 'Instalacja watchfaces, różnica VX i Elite, oraz zakup',
            cs: 'Instalace watchface, rozdíl VX a Elite a nákup',
            ja: 'ウォッチフェイスのインストール、VXとEliteの違い、購入の流れ',
            ko: '워치페이스 설치, VX와 Elite 차이, 구매 방법',
            zh: '安装表盘、VX与Elite区别及购买流程',
            ru: 'Как установить циферблаты, в чём разница между версиями VX и Elite и как проходит покупка',
            uk: 'Як встановити циферблати, у чому різниця між версіями VX та Elite і як відбувається покупка'
        },
    },
    {
        id: 'find-watchface-id',
        route: '/guides/find-watchface-id',
        icon: '🔑',
        category: 'purchase',
        tags: ['watchfaceid', 'id', 'find', 'key', 'settings', 'connectiq', 'garmin express', 'schlüssel', 'finden', 'einstellungen'],
        title: {
            en: 'How to find your WatchfaceID',
            de: 'WatchfaceID finden',
            fr: 'Trouver votre WatchfaceID',
            es: 'Cómo encontrar tu WatchfaceID',
            it: 'Come trovare la WatchfaceID',
            pt: 'Como encontrar a sua WatchfaceID',
            nl: 'Je WatchfaceID vinden',
            pl: 'Jak znaleźć WatchfaceID',
            cs: 'Jak najít WatchfaceID',
            ja: 'WatchfaceIDの見つけ方',
            ko: 'WatchfaceID 찾는 방법',
            zh: '如何查找 WatchfaceID',
            ru: 'Как найти свой WatchfaceID',
            uk: 'Як знайти свій WatchfaceID'
        },
        description: {
            en: 'Find the WatchfaceID in your watchface settings to purchase or enter a key',
            de: 'Die WatchfaceID in den Watchface-Einstellungen finden, um einen Schlüssel zu kaufen oder einzugeben',
            fr: 'Trouver la WatchfaceID dans les paramètres pour acheter ou saisir une clé',
            es: 'Encuentra la WatchfaceID en la configuración para comprar o introducir una clave',
            it: 'Trova la WatchfaceID nelle impostazioni per acquistare o inserire una chiave',
            pt: 'Encontre a WatchfaceID nas definições para comprar ou introduzir uma chave',
            nl: 'Vind de WatchfaceID in je watchface-instellingen om te kopen of een sleutel in te voeren',
            pl: 'Znajdź WatchfaceID w ustawieniach watchface, aby kupić lub wpisać klucz',
            cs: 'Najděte WatchfaceID v nastavení watchface pro nákup nebo zadání klíče',
            ja: 'ウォッチフェイス設定でWatchfaceIDを確認し、購入やキー入力に使用',
            ko: '워치페이스 설정에서 WatchfaceID를 찾아 구매 또는 키 입력',
            zh: '在表盘设置中查找 WatchfaceID，用于购买或输入密钥',
            ru: 'Найти WatchfaceID в настройках циферблата, чтобы купить или ввести ключ',
            uk: 'Знайдіть WatchfaceID у налаштуваннях циферблата, щоб придбати або ввести ключ'
        },
    },
    {
        id: 'weather',
        route: '/guides/weather',
        icon: '🌤️',
        category: 'weather',
        tags: ['weather', 'openweathermap', 'yr', 'visual crossing', 'api', 'forecast', 'wetter', 'vorhersage'],
        title: {
            en: 'Weather Provider Setup',
            de: 'Wetteranbieter einrichten',
            fr: 'Configuration des fournisseurs météo',
            es: 'Configuración de proveedores de clima',
            it: 'Configurazione provider meteo',
            pt: 'Configuração de fornecedores de clima',
            nl: 'Weerproviders instellen',
            pl: 'Konfiguracja dostawców pogody',
            cs: 'Nastavení zdrojů počasí',
            ja: '天気プロバイダーの設定',
            ko: '날씨 공급자 설정',
            zh: '天气数据源设置',
            ru: 'Настройка погодных провайдеров',
            uk: 'Налаштування провайдерів погоди'
        },
        description: {
            en: 'Configure weather data sources: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            de: 'Wetterdatenquellen konfigurieren: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            fr: 'Configurer les sources météo : Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            es: 'Configurar fuentes de datos: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            it: 'Configurare le fonti meteo: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            pt: 'Configurar fontes de dados: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            nl: 'Weerbronnen configureren: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            pl: 'Konfiguracja źródeł pogody: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            cs: 'Konfigurace zdrojů počasí: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            ja: '天気データソースの設定: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            ko: '날씨 데이터 소스 설정: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            zh: '配置天气数据源：Garmin、Yr.no、OpenWeatherMap、Visual Crossing',
            ru: 'Настройка источников погодных данных: Garmin, Yr.no, OpenWeatherMap, Visual Crossing',
            uk: 'Налаштування джерел погодних даних: Garmin, Yr.no, OpenWeatherMap, Visual Crossing'
        },
    },
    {
        id: 'tides',
        route: '/guides/tides',
        icon: '🌊',
        category: 'tides',
        tags: ['tides', 'stormglass', 'api', 'ocean', 'sea', 'gezeiten', 'meer'],
        title: {
            en: 'Tides Setup (Stormglass.io)',
            de: 'Gezeiten einrichten (Stormglass.io)',
            fr: 'Marées (Stormglass.io)',
            es: 'Mareas (Stormglass.io)',
            it: 'Maree (Stormglass.io)',
            pt: 'Marés (Stormglass.io)',
            nl: 'Getijden (Stormglass.io)',
            pl: 'Pływy (Stormglass.io)',
            cs: 'Přílivy (Stormglass.io)',
            ja: '潮汐の設定 (Stormglass.io)',
            ko: '조수 설정 (Stormglass.io)',
            zh: '潮汐设置 (Stormglass.io)',
            ru: 'Настройка приливов (Stormglass.io)',
            uk: 'Налаштування припливів (Stormglass.io)'
        },
        description: {
            en: 'Set up tide data using Stormglass.io API',
            de: 'Gezeitendaten über die Stormglass.io API einrichten',
            fr: 'Configurer les données de marée avec l\'API Stormglass.io',
            es: 'Configurar datos de mareas con la API Stormglass.io',
            it: 'Configurare i dati di marea con l\'API Stormglass.io',
            pt: 'Configurar dados de marés com a API Stormglass.io',
            nl: 'Getijdendata instellen met Stormglass.io API',
            pl: 'Konfiguracja danych pływów przez API Stormglass.io',
            cs: 'Nastavení dat o přílivech pomocí API Stormglass.io',
            ja: 'Stormglass.io APIで潮汐データを設定',
            ko: 'Stormglass.io API로 조수 데이터 설정',
            zh: '使用 Stormglass.io API 设置潮汐数据',
            ru: 'Настройка данных о приливах с помощью Stormglass.io API',
            uk: 'Налаштування даних про припливи через Stormglass.io API'
        },
    },
    {
        id: 'location',
        route: '/guides/location',
        icon: '📍',
        category: 'gps',
        tags: ['gps', 'location', 'fix', 'signal', 'position', 'standort'],
        title: {
            en: 'GPS & Location Setup',
            de: 'GPS & Standort einrichten',
            fr: 'GPS et position',
            es: 'GPS y ubicación',
            it: 'GPS e posizione',
            pt: 'GPS e localização',
            nl: 'GPS en locatie',
            pl: 'GPS i lokalizacja',
            cs: 'GPS a poloha',
            ja: 'GPSと位置の設定',
            ko: 'GPS 및 위치 설정',
            zh: 'GPS与位置设置',
            ru: 'Настройка GPS и местоположения',
            uk: 'Налаштування GPS та місця розташування'
        },
        description: {
            en: 'How to get a GPS fix and update your location',
            de: 'GPS-Fix durchführen und Standort aktualisieren',
            fr: 'Obtenir un fix GPS et mettre à jour votre position',
            es: 'Obtener señal GPS y actualizar tu ubicación',
            it: 'Eseguire un fix GPS e aggiornare la posizione',
            pt: 'Obter fix GPS e atualizar a sua localização',
            nl: 'GPS-fix uitvoeren en locatie bijwerken',
            pl: 'Wykonanie fixu GPS i aktualizacja lokalizacji',
            cs: 'Provedení GPS fixu a aktualizace polohy',
            ja: 'GPSフィックスを取得し位置を更新する方法',
            ko: 'GPS 수신 및 위치 업데이트 방법',
            zh: '获取 GPS 定位并更新您的位置',
            ru: 'Как выполнить GPS‑фиксацию и обновить своё местоположение',
            uk: 'Як виконати GPS‑фіксацію та оновити своє місце розташування'
        },
    },
    // Fonts guide vorerst aus der sichtbaren Liste entfernt
    {
        id: 'themes',
        route: '/guides/themes',
        icon: '🎨',
        category: 'general',
        tags: ['themes', 'themestring', 'color', 'dynamic', 'sunset', 'low power', 'farben', 'themestring'],
        title: {
            en: 'How Themes work',
            de: 'So funktionieren Themes',
            fr: 'Fonctionnement des thèmes',
            es: 'Cómo funcionan los temas',
            it: 'Come funzionano i temi',
            pt: 'Como funcionam os temas',
            nl: 'Hoe thema\'s werken',
            pl: 'Jak działają motywy',
            cs: 'Jak fungují témata',
            ja: 'テーマの仕組み',
            ko: '테마 작동 방식',
            zh: '主题如何工作',
            ru: 'Как работают темы',
            uk: 'Як працюють теми'
        },
        description: {
            en: 'Themestring, saving and sharing colors, dynamic theme switching (sunset, low power)',
            de: 'Themestring, Farben speichern und teilen, dynamischer Themenwechsel (Sonnenuntergang, Low Power)',
            fr: 'Chaîne de thème, enregistrer et partager les couleurs, changement dynamique (coucher, économie)',
            es: 'Cadena de tema, guardar y compartir colores, cambio dinámico (atardecer, ahorro energía)',
            it: 'Stringa tema, salvare e condividere colori, cambio dinamico (tramonto, risparmio)',
            pt: 'String de tema, guardar e partilhar cores, mudança dinâmica (pôr do sol, economia)',
            nl: 'Themastring, kleuren opslaan en delen, dynamisch wisselen (zonsondergang, energiebesparing)',
            pl: 'Ciąg motywu, zapisywanie i udostępnianie kolorów, dynamiczna zmiana (zachód słońca, oszczędzanie)',
            cs: 'Řetězec tématu, ukládání a sdílení barev, dynamické přepínání (západ, úsporný režim)',
            ja: 'テーマ文字列、色の保存と共有、動的切り替え（日没、省電力）',
            ko: '테마 문자열, 색상 저장 및 공유, 동적 전환 (일몰, 절전)',
            zh: '主题字符串、保存与分享颜色、动态切换（日落、省电）',
            ru: 'Строка темы, сохранение и обмен цветами, динамическое переключение (закат, энергосбережение)',
            uk: 'Рядок теми, збереження й обмін кольорами, динамічне перемикання (захід, енергозбереження)'
        },
    },
    {
        id: 'timezones',
        route: '/guides/timezones',
        icon: '🕒',
        category: 'setup',
        tags: ['timezone', 'utc', 'offset', 'second timezone', 'third timezone', 'zeitzone', 'utc-offset', 'zweite zeitzone'],
        title: {
            en: 'Time zones & UTC offset',
            de: 'Zeitzonen & UTC-Offset',
            fr: 'Fuseaux horaires et décalage UTC',
            es: 'Zonas horarias y desplazamiento UTC',
            it: 'Fusi orari e offset UTC',
            pt: 'Fusos horários e deslocamento UTC',
            nl: 'Tijdzones en UTC-offset',
            pl: 'Strefy czasowe i przesunięcie UTC',
            cs: 'Časová pásma a posun UTC',
            ja: 'タイムゾーンとUTCオフセット',
            ko: '시간대 및 UTC 오프셋',
            zh: '时区与 UTC 偏移',
            ru: 'Часовые пояса и смещение UTC',
            uk: 'Часові пояси та зсув UTC'
        },
        description: {
            en: 'How to configure second and third time zones using UTC offsets in minutes',
            de: 'Wie man zweite und dritte Zeitzone über UTC-Offsets in Minuten konfiguriert',
            fr: 'Configurer les 2e et 3e fuseaux horaires avec les décalages UTC en minutes',
            es: 'Configurar segunda y tercera zona horaria con desplazamiento UTC en minutos',
            it: 'Configurare seconda e terza fascia oraria con offset UTC in minuti',
            pt: 'Configurar 2.ª e 3.ª zona horária com deslocamento UTC em minutos',
            nl: 'Tweede en derde tijdzone instellen met UTC-offset in minuten',
            pl: 'Konfiguracja drugiej i trzeciej strefy czasowej (offset UTC w minutach)',
            cs: 'Konfigurace druhého a třetího časového pásma pomocí posunu UTC v minutách',
            ja: '2番目・3番目のタイムゾーンをUTCオフセット（分）で設定',
            ko: 'UTC 오프셋(분)으로 두 번째, 세 번째 시간대 설정',
            zh: '使用 UTC 偏移（分钟）配置第二、第三时区',
            ru: 'Как настроить вторую и третью зону через смещения UTC в минутах',
            uk: 'Як налаштувати другу й третю часові зони за допомогою зсуву UTC у хвилинах'
        },
    },
    {
        id: 'settings',
        route: '/guides/settings',
        icon: '⚙️',
        category: 'setup',
        tags: ['settings', 'options', 'data field', 'configuration', 'connectiq', 'heart rate', 'steps', 'weather', 'calories', 'battery', 'einstellungen', 'optionen', 'datenfeld', 'konfiguration'],
        title: {
            en: 'Watchface Settings & Data Field Options',
            de: 'Watchface-Einstellungen & Datenfeldoptionen',
            fr: 'Paramètres et options de champs de données',
            es: 'Ajustes y opciones de campos de datos',
            it: 'Impostazioni e opzioni campi dati',
            pt: 'Definições e opções de campos de dados',
            nl: 'Instellingen en dataveldopties',
            pl: 'Ustawienia i opcje pól danych',
            cs: 'Nastavení a volby datových polí',
            ja: '設定とデータフィールドオプション',
            ko: '설정 및 데이터 필드 옵션',
            zh: '设置与数据字段选项',
            ru: 'Настройки и параметры полей данных',
            uk: 'Налаштування та параметри полів даних'
        },
        description: {
            en: 'All available data field options explained, how to change settings via ConnectIQ, and troubleshooting',
            de: 'Alle verfügbaren Datenfeldoptionen erklärt, Einstellungen über ConnectIQ ändern und Fehlerbehebung',
            fr: 'Toutes les options de champs de données, comment modifier les paramètres via ConnectIQ et dépannage',
            es: 'Todas las opciones de campos de datos explicadas, cómo cambiar ajustes y solución de problemas',
            it: 'Tutte le opzioni dei campi dati spiegate, come modificare le impostazioni e risoluzione problemi',
            pt: 'Todas as opções de campos de dados, como alterar definições e resolução de problemas',
            nl: 'Alle dataveldopties uitgelegd, instellingen wijzigen via ConnectIQ en probleemoplossing',
            pl: 'Wszystkie opcje pól danych, jak zmieniać ustawienia przez ConnectIQ i rozwiązywanie problemów',
            cs: 'Všechny volby datových polí, jak změnit nastavení přes ConnectIQ a řešení problémů',
            ja: 'すべてのデータフィールドオプション、ConnectIQ経由の設定変更、トラブルシューティング',
            ko: '모든 데이터 필드 옵션 설명, ConnectIQ를 통한 설정 변경 및 문제 해결',
            zh: '所有数据字段选项说明、通过ConnectIQ更改设置以及故障排除',
            ru: 'Все параметры полей данных, как изменить настройки через ConnectIQ и устранение неполадок',
            uk: 'Усі параметри полів даних, як змінити налаштування через ConnectIQ та усунення неполадок'
        },
    },
    {
        id: 'date-format',
        route: '/guides/date-format',
        icon: '📅',
        category: 'setup',
        tags: ['date', 'format', 'datum', 'custom', 'DFT', 'date 1', 'date 2', 'watchface settings'],
        title: {
            en: 'Custom date format (Date 1 / 2 / 3)',
            de: 'Benutzerdefiniertes Datumsformat (Datum 1 / 2 / 3)',
            fr: 'Format de date personnalisé (Date 1 / 2 / 3)',
            es: 'Formato de fecha personalizado (Fecha 1 / 2 / 3)',
            it: 'Formato data personalizzato (Data 1 / 2 / 3)',
            pt: 'Formato de data personalizado (Data 1 / 2 / 3)',
            nl: 'Aangepast datumformaat (Datum 1 / 2 / 3)',
            pl: 'Niestandardowy format daty (Data 1 / 2 / 3)',
            cs: 'Vlastní formát data (Datum 1 / 2 / 3)',
            ja: 'カスタム日付形式（日付 1 / 2 / 3）',
            ko: '사용자 지정 날짜 형식 (날짜 1 / 2 / 3)',
            zh: '自定义日期格式（日期 1 / 2 / 3）',
            ru: 'Пользовательский формат даты (Дата 1 / 2 / 3)',
            uk: 'Власний формат дати (Дата 1 / 2 / 3)'
        },
        description: {
            en: 'How to configure the date display format using format codes in the watchface settings',
            de: 'So konfigurierst du das Datumsformat mit Formatcodes in den Watchface-Einstellungen',
            fr: 'Configurer le format d\'affichage de la date avec des codes dans les paramètres',
            es: 'Configurar el formato de fecha con códigos en la configuración del watchface',
            it: 'Configurare il formato della data con i codici nelle impostazioni',
            pt: 'Configurar o formato da data com códigos nas definições do watchface',
            nl: 'Datumformaat configureren met formatcodes in de watchface-instellingen',
            pl: 'Konfiguracja formatu daty za pomocą kodów w ustawieniach watchface',
            cs: 'Konfigurace formátu data pomocí kódů v nastavení watchface',
            ja: 'ウォッチフェイス設定でフォーマットコードを使って日付表示を設定',
            ko: '워치페이스 설정에서 형식 코드로 날짜 표시 설정',
            zh: '在表盘设置中使用格式代码配置日期显示',
            ru: 'Настройка формата даты с помощью кодов в настройках циферблата',
            uk: 'Налаштування формату дати за допомогою кодів у налаштуваннях циферблата'
        },
    },
    {
        id: 'switchviews',
        route: '/guides/switchviews',
        icon: '🔁',
        category: 'setup',
        tags: ['switchview', 'gesture', 'wrist', 'touch', 'view', 'ansicht', 'geste', 'handgelenk'],
        title: {
            en: 'Switchviews & gesture control',
            de: 'Switchviews & Gestensteuerung',
            fr: 'Switchviews et gestes',
            es: 'Switchviews y control por gestos',
            it: 'Switchviews e controllo gesti',
            pt: 'Switchviews e controlo por gestos',
            nl: 'Switchviews en gebarenbediening',
            pl: 'Switchviews i sterowanie gestami',
            cs: 'Switchviews a ovládání gesty',
            ja: 'Switchviewsとジェスチャー操作',
            ko: 'Switchviews 및 제스처 제어',
            zh: 'Switchviews 与手势控制',
            ru: 'Switchviews и управление жестами',
            uk: 'Switchviews і керування жестами'
        },
        description: {
            en: 'How to change views using wrist gestures or touch zones on compatible devices',
            de: 'Wie man Ansichten mit Handgelenksgesten oder Touchzonen auf kompatiblen Geräten wechselt',
            fr: 'Changer de vue par gestes du poignet ou zones tactiles sur appareils compatibles',
            es: 'Cambiar vistas con gestos de muñeca o zonas táctiles en dispositivos compatibles',
            it: 'Cambiare vista con gesti del polso o zone touch su dispositivi compatibili',
            pt: 'Mudar vistas com gestos de pulso ou zonas tácteis em dispositivos compatíveis',
            nl: 'Van weergave wisselen met polsgebaren of touchzones op compatibele apparaten',
            pl: 'Zmiana widoków gestami nadgarstka lub strefami dotyku na kompatybilnych urządzeniach',
            cs: 'Přepínání zobrazení gesty zápěstí nebo dotykovými zónami na kompatibilních zařízeních',
            ja: '対応デバイスで手首のジェスチャーやタッチゾーンで表示を切り替え',
            ko: '호환 기기에서 손목 제스처 또는 터치 영역으로 보기 전환',
            zh: '在兼容设备上用手腕手势或触控区域切换视图',
            ru: 'Как менять виды с помощью жестов запястья или сенсорных зон на совместимых устройствах',
            uk: 'Як змінювати види за допомогою жестів зап’ястя або сенсорних зон на сумісних пристроях'
        },
    },
    {
        id: 'atmosair',
        route: '/watchface/atmosair',
        icon: '🌬️',
        category: 'general',
        tags: [
            'atmosair',
            'air quality',
            'airquality',
            'aqi',
            'pm2.5',
            'pollen',
            'dust',
            'luftqualität',
            'feinstaub',
            'ozon',
        ],
        title: {
            en: 'AtmosAir – Air Quality App',
            de: 'AtmosAir – Luftqualitäts-App',
            fr: 'AtmosAir – App qualité de l’air',
            es: 'AtmosAir – App de calidad del aire',
            it: 'AtmosAir – App qualità dell’aria',
            pt: 'AtmosAir – App de qualidade do ar',
            nl: 'AtmosAir – Luchtkwaliteit-app',
            pl: 'AtmosAir – Aplikacja jakości powietrza',
            cs: 'AtmosAir – Aplikace kvality ovzduší',
            ja: 'AtmosAir – 大気質アプリ',
            ko: 'AtmosAir – 대기질 앱',
            zh: 'AtmosAir – 空气质量应用',
            ru: 'AtmosAir – приложение качества воздуха',
            uk: 'AtmosAir – додаток якості повітря',
            no: 'AtmosAir – Luftkvalitetsapp',
            sv: 'AtmosAir – Luftkvalitetsapp',
        },
        description: {
            en: 'Views, controls, EU/US AQI, pollutants and settings for the AtmosAir Garmin app',
            de: 'Ansichten, Bedienung, EU-/US-AQI, Schadstoffe und Einstellungen der AtmosAir Garmin-App',
            fr: 'Vues, commandes, AQI UE/US, polluants et réglages de l’app AtmosAir',
            es: 'Vistas, controles, AQI UE/EE.UU., contaminantes y ajustes de AtmosAir',
            it: 'Schermate, comandi, AQI UE/USA, inquinanti e impostazioni di AtmosAir',
            pt: 'Ecrãs, controlos, AQI UE/EUA, poluentes e definições do AtmosAir',
            nl: 'Schermen, bediening, EU/VS-AQI, stoffen en instellingen van AtmosAir',
            pl: 'Widoki, sterowanie, AQI UE/USA, zanieczyszczenia i ustawienia AtmosAir',
            cs: 'Zobrazení, ovládání, AQI EU/USA, škodliviny a nastavení AtmosAir',
            ja: 'AtmosAirの画面・操作・EU/US AQI・汚染物質・設定',
            ko: 'AtmosAir 화면, 조작, EU/US AQI, 오염물질 및 설정',
            zh: 'AtmosAir 界面、操作、欧盟/美国 AQI、污染物与设置',
            ru: 'Экраны, управление, AQI ЕС/США, загрязнители и настройки AtmosAir',
            uk: 'Екрани, керування, AQI ЄС/США, забрудники та налаштування AtmosAir',
            no: 'Visninger, betjening, EU/US AQI, stoffer og innstillinger for AtmosAir',
            sv: 'Vyer, kontroller, EU/US AQI, ämnen och inställningar för AtmosAir',
        },
    },
];
export function searchGuides(query, language = 'en') {
    if (!query.trim())
        return [];
    const terms = query.toLowerCase().split(/\s+/);
    return guidesRegistry.filter(guide => {
        const title = (guide.title[language] || guide.title['en'] || '').toLowerCase();
        const description = (guide.description[language] || guide.description['en'] || '').toLowerCase();
        const tags = guide.tags.map(t => t.toLowerCase());
        return terms.some(term => title.includes(term) ||
            description.includes(term) ||
            tags.some(tag => tag.includes(term) || term.includes(tag)));
    });
}
